import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, SlidersHorizontal, X } from "lucide-react";
import { BRANCH_TYPES, isCorrection } from "./LocTypeFields";

const norm = (s) => String(s ?? "").toLocaleLowerCase("tr");
const arr = (v) => (Array.isArray(v) ? v.map(String) : [String(v ?? "")]);

// Şubeler sekmesi filtreleri — sıra: Şube Türü · Bölge · Şube Kodu · Şube Adı · İl · İlçe · Taşeron · SGK İşyeri No
// Her kutuda yazarak arama (yazdıkça liste daralır); çoklu kolonlarda ayrıca onay kutularıyla tekli/toplu seçim
export const LOC_COLS = [
  { key: "branch_type", label: "Şube Türü", multi: true, options: BRANCH_TYPES, value: (l) => l.branch_type || "ŞUBE" },
  { key: "region", label: "Bölge", multi: true, value: (l) => l._region || "Bölgesiz" },
  { key: "branch_code", label: "Şube Kodu", value: (l) => l.branch_code || "" },
  { key: "name", label: "Şube Adı", value: (l) => l.name },
  { key: "city", label: "İl", multi: true, value: (l) => l.city },
  { key: "district", label: "İlçe", multi: true, value: (l) => l.district },
  { key: "subs", label: "Taşeron", multi: true, value: (l) => (l._subs?.length ? l._subs : ["atanmadı"]) },
  { key: "sgk_no", label: "SGK İşyeri No", multi: true, value: (l) => (l.sgk_no ? [l.sgk_no, "DOLU"] : ["EKSİK"]) },
];

const EMPTY = { q: "", values: [] };
const isOn = (f) => !!(f && (f.q || f.values?.length));
const match = (f, vals) => (!f.values?.length || vals.some((v) => f.values.includes(v))) && (!f.q || vals.some((v) => norm(v).includes(norm(f.q))));

export function useLocFilters(rows) {
  const [filters, setFilters] = useState({});
  const [clearTick, setClearTick] = useState(0); // "Filtreleri Temizle" → açık seçim listeleri de kapanır
  const options = useMemo(() => Object.fromEntries(LOC_COLS.filter((c) => c.multi).map((c) => [c.key, c.options || [...new Set(rows.flatMap((l) => arr(c.value(l))))].sort((a, b) => a.localeCompare(b, "tr"))])), [rows]);
  const visible = useMemo(() => rows.filter((l) => LOC_COLS.every((c) => !isOn(filters[c.key]) || match(filters[c.key], arr(c.value(l))))), [rows, filters]);
  const active = LOC_COLS.some((c) => isOn(filters[c.key]));
  const setFilter = (key, f) => setFilters((p) => ({ ...p, [key]: f }));
  const clear = () => { setFilters({}); setClearTick((t) => t + 1); };
  const restore = (snap) => { setFilters(snap || {}); setClearTick((t) => t + 1); }; // "Hataya Git" sonrası önceki filtrelere dönüş
  return { filters, setFilter, clear, restore, visible, options, active, clearTick };
}

function panelStyle(el) {
  const r = el.getBoundingClientRect();
  const below = window.innerHeight - r.bottom - 6;
  const up = below < 220 && r.top > below;
  return up ? { position: "fixed", zIndex: 60, left: r.left, bottom: window.innerHeight - r.top + 4, width: Math.max(r.width, 220), maxHeight: Math.min(260, r.top - 12) }
            : { position: "fixed", zIndex: 60, left: r.left, top: r.bottom + 4, width: Math.max(r.width, 220), maxHeight: Math.min(260, below - 6) };
}

function FilterBox({ col, f = EMPTY, set, options = [], closeTick = 0 }) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const ref = useRef(null);
  const panelRef = useRef(null);
  const q = f.q || "";
  const values = f.values || [];
  const on = isOn(f);
  const shown = useMemo(() => options.filter((o) => !q || norm(o).includes(norm(q))).slice(0, 200), [options, q]);
  useEffect(() => { setOpen(false); }, [closeTick]);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!ref.current?.contains(e.target) && !panelRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); setOpen(false); } };
    document.addEventListener("mousedown", close); document.addEventListener("keydown", onKey, true);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", onKey, true); };
  }, [open]);
  const openPanel = () => { if (!col.multi) return; if (ref.current) setStyle(panelStyle(ref.current)); setOpen(true); };
  const toggle = (o) => set({ q, values: values.includes(o) ? values.filter((x) => x !== o) : [...values, o] });
  const ph = values.length ? (values.length > 2 ? `${values.slice(0, 2).join(", ")} +${values.length - 2}` : values.join(", ")) : (col.multi ? "Yazın veya seçin…" : "Yazın…");
  return (
    <div className="min-w-0" data-testid={`loc-filter-${col.key}`}>
      <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-0.5 whitespace-nowrap">
        <span className={on ? "text-amber-700" : ""}>{col.label}</span>
        {values.length > 0 && <span className="px-1 rounded bg-amber-500 text-white text-[9px]" data-testid={`loc-filter-${col.key}-count`}>{values.length}</span>}
      </div>
      <div ref={ref} className={`relative flex items-center rounded-md border bg-white ${on ? "border-amber-400 ring-1 ring-amber-200" : "border-slate-300"}`}>
        <input value={q} onChange={(e) => { set({ q: e.target.value, values }); if (col.multi && !open) openPanel(); }} onFocus={openPanel}
          placeholder={ph} data-testid={`loc-f-${col.key}`} title={values.length ? values.join(", ") : ""}
          className={`w-full min-w-0 px-2 py-1 text-[11px] bg-transparent outline-none ${values.length ? "placeholder:text-amber-800 placeholder:font-semibold" : "placeholder:text-slate-400"}`} />
        {on && <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { set({ ...EMPTY }); setOpen(false); }} className="px-1 text-slate-400 hover:text-red-600" title="Temizle" data-testid={`loc-f-${col.key}-clear`}><X className="w-3 h-3" /></button>}
        {col.multi && <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => (open ? setOpen(false) : openPanel())} className="px-1 text-slate-400 hover:text-slate-700" title="Listeden seç" data-testid={`loc-f-${col.key}-toggle`}><ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} /></button>}
      </div>
      {open && col.multi && (
        <div ref={panelRef} style={style} className="bg-white border border-slate-200 rounded-md shadow-lg p-1.5 overflow-y-auto normal-case tracking-normal" data-testid={`loc-f-${col.key}-panel`}>
          {shown.map((o) => (
            <label key={o} className={`flex items-center gap-2 px-1.5 py-1 rounded text-[11px] cursor-pointer hover:bg-amber-50 ${values.includes(o) ? "font-bold text-amber-900" : "text-slate-700"}`} data-testid={`loc-f-${col.key}-opt-${o}`}>
              <input type="checkbox" checked={values.includes(o)} onChange={() => toggle(o)} className="accent-amber-600 w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{o}</span>
            </label>
          ))}
          {shown.length === 0 && <div className="px-1.5 py-1 text-[11px] text-slate-400">Eşleşen değer yok</div>}
          {options.length > 200 && shown.length === 200 && <div className="px-1.5 py-1 text-[10px] text-slate-400">İlk 200 değer — daraltmak için yazın</div>}
          {values.length > 0 && <button type="button" onClick={() => { set({ q, values: [] }); setOpen(false); }} className="mt-1 w-full px-2 py-1 rounded border border-red-200 text-red-700 text-[10px] font-semibold hover:bg-red-50" data-testid={`loc-f-${col.key}-unselect`}>Seçimi temizle ({values.length})</button>}
        </div>
      )}
    </div>
  );
}

export default function LocFilterBar({ lf, total }) {
  const { filters, setFilter, clear, visible, options, active, clearTick } = lf;
  const shown = visible.filter((l) => !isCorrection(l)).length;   // düzeltme farkları şubeleri sayılmaz
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 space-y-2" data-testid="loc-filter-bar">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2">
        {LOC_COLS.map((c) => <FilterBox key={c.key} col={c} f={filters[c.key]} set={(f) => setFilter(c.key, f)} options={options[c.key]} closeTick={clearTick} />)}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <SlidersHorizontal className="w-3.5 h-3.5 text-slate-500" />
        <span className="text-[10px] text-slate-500">Yazdıkça liste daralır · çoklu kolonlarda listeden tekli/toplu seçim</span>
        <span className={`ml-auto text-xs font-semibold ${active ? "text-amber-900" : "text-slate-500"}`} data-testid="loc-filter-total">{active ? `${shown} / ${total} şube` : `${total} şube`}</span>
        {active && <button type="button" onClick={clear} data-testid="loc-filter-clear" className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-red-200 bg-white text-[11px] font-semibold text-red-700 hover:bg-red-50"><X className="w-3 h-3" /> Filtreleri Temizle</button>}
      </div>
    </div>
  );
}
